import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type HTMLAttributes, type ReactNode, useId, useState } from 'react';

import { cn } from '@/shared/lib/cn';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * `plain` — обычная карточка,
   * `interactive` — реагирует на нажатие (используется, когда карточка кликабельна),
   * `outline` — без заливки, только контур: для второстепенных блоков.
   */
  appearance?: 'plain' | 'interactive' | 'outline';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const PADDINGS = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
} as const;

export function Card({
  appearance = 'plain',
  padding = 'md',
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg',
        appearance === 'outline'
          ? 'border border-border'
          : 'bg-surface shadow-sm border border-border/60',
        appearance === 'interactive' &&
          'transition-[transform,box-shadow] duration-150 active:scale-[0.985] hover:shadow-md cursor-pointer',
        PADDINGS[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

interface SectionProps {
  /**
   * Заголовок раздела. Строка или строка со значком — значок помогает искать
   * раздел глазами там, где разделов много (аптечка по назначениям).
   */
  title: ReactNode;
  /** Пояснение под заголовком — помогает без обучения понять смысл раздела. */
  subtitle?: string;
  action?: ReactNode;
  /**
   * Содержимое убирается под стрелку.
   *
   * Для разделов, куда заходят изредка: настройки и резервные копии нужны раз
   * в месяц, но занимали экран каждый день. Свёрнутый раздел оставляет на виду
   * название и пояснение — по ним видно, что внутри, а страница снова
   * охватывается взглядом. Действие справа при этом не поддерживается: вся
   * строка заголовка — кнопка раскрытия.
   */
  collapsible?: boolean;
  /** Раскрыт при первом показе. По умолчанию свёрнут — в этом весь смысл. */
  defaultOpen?: boolean;
  /**
   * Метка у заголовка — то немногое, что видно и в свёрнутом виде.
   *
   * Нужна для предупреждений, ради которых раздел вообще открывают: «копии
   * ещё нет» внутри свёрнутого раздела не увидит никто, а именно оно и есть
   * повод туда зайти.
   */
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Заголовок раздела с опциональным действием справа. */
export function Section({
  title,
  subtitle,
  action,
  collapsible = false,
  defaultOpen = false,
  badge,
  children,
  className,
}: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  if (!collapsible) {
    return (
      <section className={cn('space-y-3', className)}>
        <header className="flex items-end justify-between gap-3 px-0.5">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p>}
          </div>
          {action}
        </header>
        {children}
      </section>
    );
  }

  return (
    <section className={cn('space-y-3', className)}>
      {/* Заголовок остаётся заголовком: свёрнутый раздел должен читаться с
          экрана так же, как обычный, и находиться перебором заголовков. */}
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((value) => !value)}
          // Пояснение тоже внутри кнопки: промахнуться мимо строки заголовка
          // большим пальцем легче, чем кажется.
          className="flex min-h-11 w-full items-center justify-between gap-3 px-0.5 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[17px] font-semibold tracking-tight">{title}</span>
            {/* Пояснение живёт внутри заголовка и без явного начертания
                наследовало бы его жирность — в отличие от несвёрнутых разделов. */}
            {subtitle && (
              <span className="mt-0.5 block text-[13px] font-normal text-muted">{subtitle}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {badge}
            <ChevronDown
              size={20}
              aria-hidden
              className={cn('text-subtle transition-transform duration-200', open && 'rotate-180')}
            />
          </span>
        </button>
      </h2>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pt-0.5 pb-0.5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
